import React, { useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ProductGalleryCarouselProps {
  images: string[];
  selectedIndex: number;
  onSlideChange: (index: number) => void;
  isNew?: boolean;
  isSoldOut?: boolean;
}

export const ProductGalleryCarousel: React.FC<ProductGalleryCarouselProps> = ({
  images,
  selectedIndex,
  onSlideChange,
  isNew,
  isSoldOut,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: images.length > 1,
    skipSnaps: false,
    align: 'center',
    watchDrag: () => images.length > 1, // Only enable drag if multiple images
    duration: 25, // Smooth scroll duration (25ms = smooth, 40ms = very smooth but slower)
    dragFree: false, // Snap to slides (not free-scroll)
    containScroll: 'trimSnaps', // Removes empty space at the end
    inViewThreshold: 0.7, // Slide must be 70% in view to be selected
  });

  const [isDragging, setIsDragging] = React.useState(false);
  const [currentIndex, setCurrentIndex] = React.useState(selectedIndex);

  // Scroll to selected index when it changes externally (thumbnail click)
  useEffect(() => {
    if (emblaApi && emblaApi.selectedScrollSnap() !== selectedIndex) {
      emblaApi.scrollTo(selectedIndex);
    }
  }, [selectedIndex, emblaApi]);

  // Update currentIndex and notify parent when slide changes
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const index = emblaApi.selectedScrollSnap();
    setCurrentIndex(index);
    onSlideChange(index);
  }, [emblaApi, onSlideChange]);

  // Set up event listeners
  useEffect(() => {
    if (!emblaApi) return;

    onSelect(); // Set initial index
    emblaApi.on('select', onSelect);
    emblaApi.on('pointerDown', () => setIsDragging(true));
    emblaApi.on('pointerUp', () => setIsDragging(false));

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('pointerDown', () => setIsDragging(true));
      emblaApi.off('pointerUp', () => setIsDragging(false));
    };
  }, [emblaApi, onSelect]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!emblaApi || images.length <= 1) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        emblaApi.scrollPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        emblaApi.scrollNext();
      }
    };

    const node = emblaRef.current;
    if (node) {
      node.addEventListener('keydown', handleKeyDown);
      return () => node.removeEventListener('keydown', handleKeyDown);
    }
  }, [emblaApi, emblaRef, images.length]);

  // Preload adjacent images
  useEffect(() => {
    if (images.length <= 1) return;

    const nextIndex = (currentIndex + 1) % images.length;
    const prevIndex = (currentIndex - 1 + images.length) % images.length;

    [images[nextIndex], images[prevIndex]].forEach(imgSrc => {
      if (imgSrc && !imgSrc.includes('.mp4')) {
        const img = new Image();
        img.src = imgSrc;
      }
    });
  }, [currentIndex, images]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  // Single image - no carousel needed
  if (images.length === 1) {
    return (
      <div className="relative bg-gray-100 aspect-square overflow-hidden w-full">
        {images[0].includes('.mp4') ? (
          <video
            src={images[0]}
            className="w-full h-full object-contain"
            controls
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <img
            src={images[0] || '/assets/placeholder.svg'}
            alt="Product"
            onError={(e) => {
              const t = e.currentTarget;
              if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
            }}
            className="w-full h-full object-contain"
          />
        )}

        {isSoldOut && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <div className="bg-black/70 text-white font-mono text-sm tracking-[0.3em] uppercase px-8 py-3 select-none">
              Sold Out
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {/* Stable aspect ratio wrapper to prevent layout shift */}
      <div className="aspect-square w-full">
        {/* Main Carousel */}
        <div
          ref={emblaRef}
          className={`overflow-hidden group h-full ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          role="region"
          aria-label="Product image gallery"
          aria-live="polite"
          tabIndex={0}
        >
          <div className="flex h-full transition-transform duration-300 ease-out will-change-transform">
            {images.map((img, idx) => (
              <div
                key={idx}
                className="min-w-full relative bg-gray-100 h-full flex-shrink-0"
              role="group"
              aria-roledescription="slide"
              aria-label={`Image ${idx + 1} of ${images.length}`}
            >
              {img.includes('.mp4') ? (
                <video
                  src={img}
                  className="w-full h-full object-contain select-none"
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  onMouseDown={(e) => {
                    // Prevent drag on video controls
                    if ((e.target as HTMLElement).tagName === 'VIDEO') {
                      e.stopPropagation();
                    }
                  }}
                />
              ) : (
                <img
                  src={img || '/assets/placeholder.svg'}
                  alt={`Product view ${idx + 1}`}
                  onError={(e) => {
                    const t = e.currentTarget;
                    if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
                  }}
                  className="w-full h-full object-contain select-none"
                  draggable={false}
                  loading={Math.abs(idx - currentIndex) <= 1 ? 'eager' : 'lazy'}
                />
              )}

            </div>
          ))}
        </div>

        {/* OUT OF STOCK overlay - on all slides */}
        {isSoldOut && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <div className="bg-black/70 text-white font-mono text-sm tracking-[0.3em] uppercase px-8 py-3 select-none">
              Sold Out
            </div>
          </div>
        )}

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={scrollPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2
                         w-10 h-10 rounded-full bg-white/90 hover:bg-white
                         shadow-md flex items-center justify-center
                         transition-opacity duration-300
                         opacity-0 group-hover:opacity-100
                         md:opacity-100 z-10"
              aria-label="Previous image"
            >
              <ChevronLeft size={20} className="text-gray-900" />
            </button>
            <button
              onClick={scrollNext}
              className="absolute right-4 top-1/2 -translate-y-1/2
                         w-10 h-10 rounded-full bg-white/90 hover:bg-white
                         shadow-md flex items-center justify-center
                         transition-opacity duration-300
                         opacity-0 group-hover:opacity-100
                         md:opacity-100 z-10"
              aria-label="Next image"
            >
              <ChevronRight size={20} className="text-gray-900" />
            </button>
          </>
        )}
        </div>
      </div>

      {/* Dot Indicators */}
      {images.length > 1 && (
        <div className="flex justify-center gap-2 my-4">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollTo(idx)}
              className={`
                h-1.5 rounded-full transition-all duration-300
                ${idx === currentIndex
                  ? 'bg-black w-8'
                  : 'bg-gray-300 w-1.5 hover:bg-gray-400'
                }
              `}
              aria-label={`Go to image ${idx + 1}`}
              aria-current={idx === currentIndex ? 'true' : 'false'}
            />
          ))}
        </div>
      )}
    </div>
  );
};
